/**
 * Metas de ahorro.
 *
 * La idea que gobierna la pantalla: **lo apartado no es dinero para gastar**. Por
 * eso el total apartado se descuenta del disponible en el panel de inicio, y aquí
 * se ve cuánto falta y **cuánto habría que apartar al mes** para llegar a tiempo.
 * Un objetivo sin fecha ni cuota mensual es un deseo, no una meta.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PartyPopper, PiggyBank, Plus, Target } from 'lucide-react';
import { useState } from 'react';

import { daysBetweenISO, formatCOP, parseAmountFromText, toLocalTimeHHmm, todayISO } from '@eldescuadre/core';
import type { Goal } from '@eldescuadre/contracts';

import {
  contributeToGoal,
  createGoal,
  deleteGoal,
  getGoalContributions,
  getGoals,
  getGoalsSummary,
} from '../lib/endpoints';
import { ApiError } from '../lib/api';
import { Card, EmptyState, ErrorNote, Field, Loading, Modal, Money, Note, Progress, Stat, SubmitButton } from '../components/ui';

/** Colores disponibles para las metas, de la paleta de la marca. */
const GOAL_COLORS = ['#ECD082', '#20A9E6', '#10B981', '#EB525B', '#8B5CF6', '#FFF1B5'];

export function GoalsPage(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [contributing, setContributing] = useState<Goal | null>(null);
  const [celebrating, setCelebrating] = useState<string | null>(null);

  const goals = useQuery({ queryKey: ['goals'], queryFn: getGoals });
  const summary = useQuery({ queryKey: ['goals', 'summary'], queryFn: getGoalsSummary });

  const refresh = async (): Promise<void> => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['goals'] }),
      queryClient.invalidateQueries({ queryKey: ['summary'] }),
    ]);
  };

  const remove = useMutation({ mutationFn: deleteGoal, onSuccess: refresh });

  const active = (goals.data?.goals ?? []).filter((g) => g.status === 'ACTIVE');
  const achieved = (goals.data?.goals ?? []).filter((g) => g.status === 'ACHIEVED');

  return (
    <div className="space-y-4 animate-ed-fade-in">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Metas</h1>
          <p className="mt-0.5 text-sm text-ed-text-muted">Lo que estás apartando y para qué.</p>
        </div>
        <button
          type="button"
          className="ed-btn-primary !min-h-[44px] !w-auto shrink-0 !px-4"
          onClick={() => setCreating(true)}
        >
          <Plus size={18} aria-hidden="true" />
          Meta
        </button>
      </header>

      {summary.isLoading && <Loading label="Cargando tus metas…" />}

      {summary.data && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Stat
              label="Tienes apartado"
              value={<Money amount={summary.data.totalSaved} />}
              hint={
                summary.data.totalTarget > 0 ? (
                  <>
                    de <Money amount={summary.data.totalTarget} /> en metas activas
                  </>
                ) : (
                  'Todavía sin metas'
                )
              }
              tone="gold"
            />

            <Stat
              label="Para llegar a tiempo"
              value={
                summary.data.requiredMonthly > 0 ? (
                  <Money amount={summary.data.requiredMonthly} />
                ) : (
                  '—'
                )
              }
              hint={
                summary.data.requiredMonthly > 0
                  ? 'al mes, según las fechas que pusiste'
                  : 'Ponle fecha a tus metas para calcularlo'
              }
            />
          </div>

          {summary.data.monthContributions > 0 && (
            <Note>
              Este mes has apartado <Money amount={summary.data.monthContributions} className="font-semibold" />.
              Ese dinero <strong>no cuenta como disponible para gastar</strong>: ya tiene dueño.
            </Note>
          )}

          <Card title="Tus metas">
            {goals.isLoading && <Loading />}

            {active.length === 0 && achieved.length === 0 && (
              <EmptyState
                title="Sin metas todavía"
                message="Un viaje, un computador, un fondo para imprevistos. Ponle un monto y una fecha, y te diremos cuánto apartar cada mes."
                action={
                  <button
                    type="button"
                    className="ed-btn-primary !w-auto !px-4"
                    onClick={() => setCreating(true)}
                  >
                    Crear la primera
                  </button>
                }
              />
            )}

            <ul className="space-y-3">
              {active.map((goal) => (
                <li key={goal.id}>
                  <div className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="flex items-center gap-2 font-medium">
                          <Target
                            size={15}
                            className="shrink-0"
                            style={{ color: goal.color }}
                            aria-hidden="true"
                          />
                          <span className="truncate">{goal.name}</span>
                        </p>
                        <p className="mt-0.5 text-xs text-ed-text-muted">
                          <Money amount={goal.savedAmount} /> de <Money amount={goal.targetAmount} />
                          {goal.targetDate && (
                            <>
                              {' · '}
                              {(() => {
                                const days = daysBetweenISO(todayISO(), goal.targetDate);
                                return days > 0 ? `faltan ${days} días` : 'fecha cumplida';
                              })()}
                            </>
                          )}
                        </p>
                      </div>

                      <span
                        className="shrink-0 font-display text-lg font-bold"
                        style={{ color: goal.color }}
                      >
                        {Math.round(goal.percentSaved)} %
                      </span>
                    </div>

                    <div className="mt-2">
                      <Progress
                        percent={goal.percentSaved}
                        tone={goal.percentSaved >= 100 ? 'success' : 'gold'}
                        label={`${goal.name}: ${Math.round(goal.percentSaved)} por ciento`}
                      />
                    </div>

                    {goal.requiredMonthlyAmount !== null && goal.requiredMonthlyAmount > 0 && (
                      <p className="mt-2 text-xs text-ed-text-muted">
                        Apartando <Money amount={goal.requiredMonthlyAmount} /> al mes llegas a
                        tiempo.
                      </p>
                    )}

                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        className="ed-btn-primary !min-h-[40px] flex-1 text-xs"
                        onClick={() => setContributing(goal)}
                      >
                        <PiggyBank size={14} aria-hidden="true" />
                        Apartar
                      </button>
                      <button
                        type="button"
                        className="ed-btn-ghost !min-h-[40px] !w-auto !px-3 text-xs text-ed-coral-light"
                        onClick={() => {
                          if (window.confirm(`¿Eliminar la meta «${goal.name}»? Lo apartado no se pierde, sigue en tus cuentas.`)) {
                            remove.mutate(goal.id);
                          }
                        }}
                        aria-label={`Eliminar la meta ${goal.name}`}
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </Card>

          {achieved.length > 0 && (
            <Card title="Cumplidas">
              <ul className="divide-y divide-ed-bg-border/25">
                {achieved.map((goal) => (
                  <li key={goal.id} className="flex items-center justify-between gap-3 py-2.5">
                    <span className="flex min-w-0 items-center gap-2 text-sm">
                      <PartyPopper size={15} className="shrink-0 text-ed-gold" aria-hidden="true" />
                      <span className="truncate">{goal.name}</span>
                    </span>
                    <Money amount={goal.savedAmount} className="shrink-0 text-sm text-ed-success" />
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-xs text-ed-text-muted">
                Lo conseguiste. El dinero sigue en tus cuentas: la meta solo marcaba para qué era.
              </p>
            </Card>
          )}
        </>
      )}

      {creating && (
        <NewGoalModal
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false);
            await refresh();
          }}
        />
      )}

      {contributing && (
        <ContributeModal
          goal={contributing}
          onClose={() => setContributing(null)}
          onSaved={async (justAchieved) => {
            const name = contributing.name;
            setContributing(null);
            await refresh();
            if (justAchieved) setCelebrating(name);
          }}
        />
      )}

      {celebrating && (
        <Modal open onClose={() => setCelebrating(null)} title="¡Meta cumplida!">
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-ed-gold/20 text-ed-gold">
              <PartyPopper size={32} aria-hidden="true" />
            </span>
            <p className="font-display text-lg font-bold">Lo lograste</p>
            <p className="text-sm text-ed-text-silver">
              Completaste «{celebrating}».
            </p>
            <p className="text-xs text-ed-text-muted">
              Ahorrar cuesta. Que quede registrado.
            </p>
            <SubmitButton type="button" onClick={() => setCelebrating(null)}>
              Seguir
            </SubmitButton>
          </div>
        </Modal>
      )}
    </div>
  );
}

/** Crear una meta. */
function NewGoalModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => Promise<void>;
}): React.JSX.Element {
  const [name, setName] = useState('');
  const [targetText, setTargetText] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [color, setColor] = useState(GOAL_COLORS[0] ?? '#ECD082');

  const target = parseAmountFromText(targetText) ?? 0;
  const daysToTarget = targetDate ? daysBetweenISO(todayISO(), targetDate) : null;
  const monthsToTarget = daysToTarget !== null ? Math.max(1, Math.ceil(daysToTarget / 30)) : null;
  const monthly = monthsToTarget ? Math.ceil(target / monthsToTarget) : null;

  const save = useMutation({
    mutationFn: () =>
      createGoal({
        name: name.trim(),
        targetAmount: target,
        targetDate: targetDate || undefined,
        color,
      }),
    onSuccess: onSaved,
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title="Nueva meta"
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={name.trim().length === 0 || target <= 0}
          onClick={() => save.mutate()}
        >
          Crear meta
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <Field label="¿Para qué estás ahorrando?" htmlFor="nombre-meta">
          <input
            id="nombre-meta"
            className="ed-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Viaje a San Andrés"
            autoFocus
            autoComplete="off"
          />
        </Field>

        <Field label="¿Cuánto necesitas?" htmlFor="monto-meta">
          <input
            id="monto-meta"
            className="ed-input ed-money"
            inputMode="numeric"
            value={targetText}
            onChange={(e) => setTargetText(e.target.value)}
            placeholder="2.000.000"
            autoComplete="off"
          />
        </Field>

        <Field
          label="¿Para cuándo? (opcional)"
          htmlFor="fecha-meta"
          hint="Con una fecha podemos decirte cuánto apartar cada mes."
        >
          <input
            id="fecha-meta"
            type="date"
            className="ed-input"
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
          />
        </Field>

        {target > 0 && monthly !== null && daysToTarget !== null && daysToTarget > 0 && (
          <Note>
            Para llegar el {targetDate} tendrías que apartar{' '}
            <strong>{formatCOP(monthly)}</strong> al mes durante {monthsToTarget}{' '}
            {monthsToTarget === 1 ? 'mes' : 'meses'}.
          </Note>
        )}

        <div>
          <p className="ed-label mb-2">Color</p>
          <div className="flex gap-2">
            {GOAL_COLORS.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setColor(option)}
                aria-label={`Usar el color ${option}`}
                aria-pressed={color === option}
                className={`h-9 w-9 rounded-full border-2 transition-transform ${
                  color === option ? 'scale-110 border-ed-text-white' : 'border-transparent'
                }`}
                style={{ backgroundColor: option }}
              />
            ))}
          </div>
        </div>

        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}

/** Apartar dinero en una meta. */
function ContributeModal({
  goal,
  onClose,
  onSaved,
}: {
  goal: Goal;
  onClose: () => void;
  onSaved: (justAchieved: boolean) => Promise<void>;
}): React.JSX.Element {
  const [amountText, setAmountText] = useState('');
  const [notes, setNotes] = useState('');

  const amount = parseAmountFromText(amountText) ?? 0;
  const after = goal.savedAmount + amount;

  const history = useQuery({
    queryKey: ['goals', 'contributions', goal.id],
    queryFn: () => getGoalContributions(goal.id),
  });

  const save = useMutation({
    mutationFn: () =>
      contributeToGoal(goal.id, {
        amount,
        date: todayISO(),
        time: toLocalTimeHHmm(new Date()),
        notes: notes.trim() || undefined,
      }),
    onSuccess: (data) => onSaved(data.justAchieved),
  });

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Apartar para ${goal.name}`}
      footer={
        <SubmitButton
          type="button"
          pending={save.isPending}
          disabled={amount <= 0}
          onClick={() => save.mutate()}
        >
          Apartar {amount > 0 && formatCOP(amount)}
        </SubmitButton>
      }
    >
      <div className="space-y-4">
        <p className="rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 px-3 py-2 text-sm">
          Llevas <Money amount={goal.savedAmount} className="font-semibold" /> de{' '}
          <Money amount={goal.targetAmount} />. Te faltan <Money amount={goal.remaining} />.
        </p>

        <Field
          label="¿Cuánto apartas?"
          htmlFor="aporte"
          hint="Puedes escribirlo como quieras: «200k», «200.000»."
        >
          <input
            id="aporte"
            className="ed-input ed-money"
            inputMode="numeric"
            value={amountText}
            onChange={(e) => setAmountText(e.target.value)}
            placeholder="200.000"
            autoFocus
            autoComplete="off"
          />
        </Field>

        {amount > 0 && (
          <div className="space-y-1.5 rounded-ed border border-ed-bg-border/30 bg-ed-bg-elevated/40 p-3 text-sm">
            <div className="flex justify-between">
              <span className="text-ed-text-muted">Quedaría en</span>
              <Money amount={after} className="font-semibold" />
            </div>
            <div className="flex justify-between">
              <span className="text-ed-text-muted">Faltaría</span>
              <Money amount={Math.max(0, goal.targetAmount - after)} className="font-semibold" />
            </div>
            {after >= goal.targetAmount && (
              <p className="flex items-center gap-1.5 pt-1 text-ed-gold">
                <PartyPopper size={14} aria-hidden="true" />
                Con esto completas la meta.
              </p>
            )}
          </div>
        )}

        <Field label="Notas (opcional)" htmlFor="notas-aporte">
          <input
            id="notas-aporte"
            className="ed-input"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="De dónde salió este dinero…"
            autoComplete="off"
          />
        </Field>

        {history.data && history.data.contributions.length > 0 && (
          <div>
            <p className="ed-label mb-2">Aportes anteriores</p>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-sm">
              {history.data.contributions.slice(0, 8).map((entry) => (
                <li key={entry.id} className="flex justify-between text-ed-text-muted">
                  <span>{entry.date}</span>
                  <Money amount={entry.amount} />
                </li>
              ))}
            </ul>
          </div>
        )}

        <Note>
          Apartar <strong>no saca el dinero de tus cuentas</strong>: marca ese dinero como
          comprometido para que el saldo disponible no te engañe. Si quieres moverlo de verdad,
          haz una transferencia.
        </Note>

        {error && <ErrorNote message={error.detailMessage} />}
      </div>
    </Modal>
  );
}
